import { observer } from 'mobx-react';
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { cn } from '@core/utils/cn';
import { UserContext } from '..';
import { useError } from '../../../components/hooks/useError';
import { Api } from '../../../core/api';
import { injectTSDI } from '../../../core/tsdi';

interface FlagCopy {
    title: string;
    description?: string;
}

const FLAG_COPY: Record<string, FlagCopy> = {
    WITHINGS: {
        title: 'Withings',
        description: 'Import steps, sleep and workouts from Withings devices.'
    },
    HUAWEI_HEALTH: { title: 'Huawei Health' },
    SAMSUNG_HEALTH: {
        title: 'Samsung Health',
        description: 'Import activity from Samsung Health on Android.'
    },
    SUUNTO: { title: 'Suunto' },
    ZEPP: { title: 'Zepp' },
    GOOGLE_HEALTH: {
        title: 'Google Health Connect',
        description: 'Replaces the legacy Google Fit connection on Android 14+.'
    },
    UNIFIED_WORKOUT: {
        title: 'Unified workout model',
        description: 'Route all synced workouts through the new pipeline. Turn off if imports look wrong.'
    },
    COACH_MO: {
        title: 'CoachMo',
        description: 'Personal AI coach Mo in the mobile app.'
    }
};

const orderedKeys = (flags: Record<string, boolean>) => {
    const known = Object.keys(FLAG_COPY).filter((key) => key in flags);
    const unknown = Object.keys(flags).filter((key) => !(key in FLAG_COPY)).sort();
    return [...known, ...unknown];
};

export const ProductFlagsCard = observer(() => {
    const user = React.useContext(UserContext);
    const { mobileClient } = injectTSDI(Api);
    const showError = useError();
    const [flags, setFlags] = React.useState<Record<string, boolean>>(user.featureFlags ?? {});
    const latestFlags = React.useRef(flags);
    const [saving, setSaving] = React.useState<Record<string, boolean>>({});

    const updateFlags = (next: Record<string, boolean>) => {
        latestFlags.current = next;
        setFlags(next);
    };

    React.useEffect(() => {
        updateFlags(user.featureFlags ?? {});
    }, [user.id, user.featureFlags]);

    async function toggle(key: string) {
        if (saving[key]) {
            return;
        }
        const previousValue = !!latestFlags.current[key];
        const optimistic = { ...latestFlags.current, [key]: !previousValue };
        updateFlags(optimistic);
        setSaving((state) => ({ ...state, [key]: true }));
        try {
            const result =
                await mobileClient.supportApi.userFeatureFlagControllerUpdateFeatureFlags(
                    user.id,
                    { featureFlags: { [key]: !previousValue } }
                );
            updateFlags({
                ...latestFlags.current,
                [key]: result.data.featureFlags?.[key] ?? !previousValue
            });
        } catch (e) {
            updateFlags({ ...latestFlags.current, [key]: previousValue });
            showError(e);
        } finally {
            setSaving((state) => ({ ...state, [key]: false }));
        }
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Product flags</CardTitle>
                <CardDescription>
                    Per-user feature toggles. Changes apply on the next app sync.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col divide-y divide-border">
                    {orderedKeys(flags).map((key) => {
                        const copy = FLAG_COPY[key];
                        const id = `flag-${key}`;
                        return (
                            <div key={key} className="flex items-center justify-between gap-6 py-3">
                                <div className="flex min-w-0 flex-col gap-0.5">
                                    <Label
                                        htmlFor={id}
                                        className={cn(!copy && 'font-mono text-sm')}
                                    >
                                        {copy?.title ?? key}
                                    </Label>
                                    {copy?.description && (
                                        <span className="text-sm text-muted-foreground">
                                            {copy.description}
                                        </span>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-3">
                                    <span className="min-w-14 text-right text-xs text-muted-foreground">
                                        {saving[key] ? 'Saving…' : flags[key] ? 'On' : 'Off'}
                                    </span>
                                    <Switch
                                        id={id}
                                        checked={!!flags[key]}
                                        disabled={!!saving[key]}
                                        onCheckedChange={() => void toggle(key)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
});
